import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ENV } from 'src/providers/environment.provider';
import { Environment } from 'src/types/environment';
import { LsApplicationTenant } from 'src/types/ls-application-tenant';
import { LsApplicationTenantsResponse } from 'src/types/ls-application-tenants-response';
import { LsLicense } from 'src/types/ls-license';
import { LsLicenseTenantConfig } from 'src/types/ls-license-tenant-config';
import { LoginService } from 'login';
import { SKIP_INTERCEPTOR } from 'src/constants';
import { TenantConfig } from 'src/types/tenant-config';
import { UpdateClientConfigRequest } from 'src/types/client-config';

const LICENSE_EXPIRE_DURATION = 10000;

interface CognitoSsoConfig {
  tenantId: string;
  tenantName: string;
  sso: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class TenantService {
  private license?: Promise<LsLicense>;

  constructor(
    @Inject(ENV) private readonly env: Environment,
    private readonly http: HttpClient,
    private readonly loginService: LoginService
  ) { }

  /**
   * Gets all the application tenants.
   * @returns All the application tenants.
   */
  public async getAll(): Promise<LsApplicationTenant[]> {
    const authHeaders = await this.loginService.getHttpAuthorisationHeaders();
    const url = this.env.licenseServerURL + 'ApplicationTenants';
    const headers = authHeaders.append(SKIP_INTERCEPTOR, 'true');
    const response = await firstValueFrom(this.http.post<LsApplicationTenantsResponse>(url, {
      ApplicationShortName: this.env.appShortName
    }, { headers: headers }));
    return response.tenants;
  }

  public async getAuthorizationHeaders(): Promise<HttpHeaders> {
    const license = await this.getLicense();
    const authHeaders = await this.loginService.getHttpAuthorisationHeaders();
    const headers = authHeaders.append('license', license.token);
    return headers;
  }

  public async getApiUrl(): Promise<string> {
    const license = await this.getLicense();
    const tenantConfig: LsLicenseTenantConfig = JSON.parse(license.tenantConfig);
    return `https://${tenantConfig.apiid}.execute-api.${tenantConfig.region}.amazonaws.com/${tenantConfig.stage}/`
  }

  public async getWebsocketUrl(): Promise<string> {
    const license = await this.getLicense();
    const tenantConfig = <LsLicenseTenantConfig>JSON.parse(license.tenantConfig);
    return `wss://${tenantConfig.websocketid}.execute-api.${tenantConfig.region}.amazonaws.com/${tenantConfig.websocketstage}`
  }

  private async getLicense(): Promise<LsLicense> {
    if (!this.license || this.getIsLicenseExpired(await this.license)) {
      this.license = this._getLicense();
    }

    return this.license;
  }

  public async getTenantConfig(): Promise<TenantConfig> {
    return await firstValueFrom(this.http.get<TenantConfig>('/config'));
  }

  public async updateTenantConfig(newConfig: Partial<UpdateClientConfigRequest>): Promise<void> {
    await firstValueFrom(this.http.put<TenantConfig>('/config', newConfig));
  }

  private getIsLicenseExpired(license?: LsLicense): boolean {
    if (!license) { return true; }
    const now = new Date();
    const expiryDate = new Date(license.expiryUtc);
    const remainingMilliseconds = expiryDate.getTime() - now.getTime();
    return remainingMilliseconds < LICENSE_EXPIRE_DURATION;
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  private async _getSsoConfig(): Promise<CognitoSsoConfig> {
    const email = await this.loginService.getUserEmail();
    const headers = (await this.loginService.getHttpAuthorisationHeaders()).append(SKIP_INTERCEPTOR, 'true');
    const config = await firstValueFrom(this.http.get<CognitoSsoConfig>(
      this.env.licenseServerURL + `TenantApplications?email=${email}&appName=${this.env.appShortName}`, { headers: headers }));
    return config;
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  private async _getLicense(): Promise<LsLicense> {
    const url = this.env.licenseServerURL + 'ApplicationTenantToken';

    const authHeaders = await this.loginService.getHttpAuthorisationHeaders();
    const headers = authHeaders.append(SKIP_INTERCEPTOR, 'true');

    return await firstValueFrom(this.http.post<LsLicense>(url, {
      ApplicationShortName: this.env.appShortName,
      TenantId: await this._getSsoConfig().then(x => { return x.tenantId })
    }, { headers: headers }));
  }
}